import { normalizedKeys } from '../../config/NormalizedValues';
import tabsGroup from '../../config/TabsGroup';
import { ConvertedData, ShotExtra, Target } from '../../store/Session/SessionModel';

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shotQualityRanges } from '../../config/CombineTutorial';
import useAutoFitRow from '../../hooks/useAutoFitRow';
import sessionSlice from '../../store/Session/SessionSlice';
import { RootState } from '../../store/store';
import { LmLocation } from '../../models';
import ShotValueKey from './ShotValueKey';
import './style.css';

interface Props {
  dataTableType: 'ball-data' | 'club-data' | 'extra-long-table' | 'key-data';
  tabSelected: 'ball-data' | 'club-data' | 'key-data';
  target: Target;
}

interface NumbersRowProps {
  group: any[];
  shot: ShotExtra;
}

const NumbersRow = ({ group, shot }: NumbersRowProps) => {
  const numbersRef = useAutoFitRow<HTMLDivElement>();
  // KWSA-24: Simulator mode shows normalized values in white (no blue highlight).
  const location = useSelector((state: RootState) => state.session.location);
  const isSimulator = location === LmLocation.SIMULATOR;

  return (
    <div className="numbers" ref={numbersRef}>
      {group.map(tabItem => {
        if (tabItem.avgKey === 'quality') {
          return (
            <div className="text-wrapper" key={tabItem.avgKey}>
              <div className={`combine-item`}>
                <div className={`combine-item-score ${shotQualityRanges(shot.shotQuality).quality}`}>
                  {shot.shotQuality?.toFixed() ?? '---'}
                </div>
              </div>
            </div>
          );
        }
        const isNormalizedCell = shot.isNormalized && normalizedKeys.includes(tabItem.avgKey) && !isSimulator;
        return (
          <div className={`text-wrapper ${isNormalizedCell ? 'normalized' : ''}`} key={tabItem.avgKey}>
            <ShotValueKey shot={shot[tabItem.shotKey as keyof ShotExtra] as ConvertedData} round={tabItem?.round} fixed={tabItem?.fixed} />
          </div>
        );
      })}
    </div>
  );
};

const DataTargetTable = ({ dataTableType, tabSelected, target }: Props): JSX.Element => {
  const dispatch = useDispatch();

  const shotRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const latestSelectedShotIndex = target.shots
    .filter(shot => shot.isSelected)
    .reduce<{ shotIndex: number; ts: number } | null>((latest, shot) => {
      const ts = shot.selectedTimestamp ?? 0;
      if (!latest || ts >= latest.ts) {
        return { shotIndex: shot.shotIndex, ts };
      }
      return latest;
    }, null)?.shotIndex;

  useEffect(() => {
    if (latestSelectedShotIndex !== undefined && shotRefs.current[latestSelectedShotIndex]) {
      shotRefs.current[latestSelectedShotIndex]!.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [latestSelectedShotIndex]);

  const getRowStyle = (isSelected: boolean, clubColor: string) => {
    return isSelected ? { border: '1px solid', borderColor: clubColor, borderRadius: '2px' } : {};
  };

  const handleShotClick = (shot: ShotExtra) => {
    dispatch(
      sessionSlice.actions.setClubShotIsSelected({
        isSelected: !shot.isSelected,
        clubId: shot.clubId,
        shotIndex: shot.shotIndex,
      }),
    );
  };

  const setClubShotIsActive = (event: any, shot: ShotExtra) => {
    event.stopPropagation();
    dispatch(
      sessionSlice.actions.setClubShotIsActive({
        isActive: !shot.isActive,
        targetId: target.targetId,
        shotIndex: shot.shotIndex,
      }),
    );
  };
  const [group, setGroup] = useState(tabsGroup[tabSelected]);
  useEffect(() => {
    setGroup([...tabsGroup[tabSelected]]);
    if (!target.targetId && target.totalShots > 0) {
      const newTabItems = tabsGroup[tabSelected]?.filter((item: any) => {
        const keysIgnore = ['quality', 'distanceToPin'];
        return !keysIgnore.includes(item.avgKey);
      });
      setGroup([...newTabItems]);
    }
  }, [target, tabSelected]);
  // Newest shot first, as the Golf App lists them. The shotIndex fallback covers
  // sessions that report one identical timestamp for every shot, matching the club
  // tables (generateClubGroup).
  const sorted = [...target.shots].sort((a, b) => b.timestamp - a.timestamp || b.shotIndex - a.shotIndex);
  return (
    <div className="data-table">
      {sorted
        .map((shot: ShotExtra, index: number) => (
          <div
            className="row"
            key={shot.shotIndex}
            ref={el => (shotRefs.current[shot.shotIndex] = el)}
            style={getRowStyle(shot.isSelected, shot.clubColor)}
            onClick={() => handleShotClick(shot)}
          >
            <div className={`is-active-${shot.isActive}`}>
              <div className="icons">
                <div className="wrap-club-name">
                  <div className="toggle-club">
                    <div
                      className={`circle-club-color`}
                      style={{
                        backgroundColor: shot.isActive ? shot.clubColor : 'transparent',
                      }}
                    >
                      <div className="club-icon" onClick={event => setClubShotIsActive(event, shot)}>
                        {shot.isActive && shot.clubIcon}
                        {!shot.isActive && <img alt="" src="/img/fs_visible-false.svg" />}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="star-img-wrapper">
                  <img alt="" src={shot.isFavorite ? '/img/backnine-star-green.svg' : '/img/fs_star_outline.svg'} />
                </div>

                <div className="shot-number-text">{index + 1}</div>
              </div>

              <NumbersRow group={group} shot={shot} />
            </div>
          </div>
        ))}
    </div>
  );
};

export default DataTargetTable;
